/**
 * Runs inside the preview stage's iframe (previewMode === "device"). Gives the app the
 * device's safe-area insets, draws the decorative status bar (real local time), Dynamic
 * Island and home indicator, and mirrors the app's route into the stage's address bar so a
 * refresh keeps the current screen.
 */
import type { DeviceSpec } from "./mode";

export const URL_MESSAGE = "status:preview-url";

function postUrl(): void {
  try {
    window.parent.postMessage({ type: URL_MESSAGE, url: location.pathname + location.search + location.hash }, location.origin);
  } catch {
    /* parent gone */
  }
}

function timeText(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).replace(/\s?[AP]M$/i, "");
}

export function installDevice(device: DeviceSpec): void {
  const root = document.documentElement;
  root.classList.add("device-embed", `device-embed--${device.id}`);
  root.style.setProperty("--sa-top", `${device.top}px`);
  root.style.setProperty("--sa-bottom", `${device.bottom}px`);
  root.style.setProperty("--sa-left", "0px");
  root.style.setProperty("--sa-right", "0px");

  const chrome = document.createElement("div");
  chrome.className = "device-chrome";
  chrome.setAttribute("aria-hidden", "true");
  chrome.innerHTML = `
    <div class="device-status${device.island ? " device-status--island" : ""}">
      <span class="device-status__time"></span>
      <span class="device-status__icons">
        <svg viewBox="0 0 18 12" width="18" height="12"><rect x="0" y="8" width="3" height="4" rx="1"/><rect x="5" y="5.5" width="3" height="6.5" rx="1"/><rect x="10" y="3" width="3" height="9" rx="1"/><rect x="15" y="0" width="3" height="12" rx="1"/></svg>
        <svg viewBox="0 0 16 12" width="16" height="12"><path d="M8 2.6c2.4 0 4.6.9 6.2 2.5l1.3-1.3A10.6 10.6 0 0 0 8 .8 10.6 10.6 0 0 0 .5 3.8l1.3 1.3A8.8 8.8 0 0 1 8 2.6zm0 3.6c1.4 0 2.7.5 3.7 1.4l1.3-1.3a7.1 7.1 0 0 0-10 0l1.3 1.3c1-.9 2.3-1.4 3.7-1.4zM8 9.8l2.2-2.2a3.1 3.1 0 0 0-4.4 0z"/></svg>
        <svg viewBox="0 0 27 12" width="27" height="12"><rect x=".5" y=".5" width="23" height="11" rx="3.2" fill="none" stroke="currentColor" opacity=".45"/><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="24.6" y="4" width="1.8" height="4" rx=".9" opacity=".45"/></svg>
      </span>
    </div>
    ${device.island ? '<div class="device-island"></div>' : ""}
    ${device.bottom ? '<div class="device-home"></div>' : ""}`;
  const mount = () => document.body.appendChild(chrome);
  if (document.body) mount();
  else document.addEventListener("DOMContentLoaded", mount, { once: true });

  const time = () => {
    const el = chrome.querySelector(".device-status__time");
    if (el) el.textContent = timeText(new Date());
  };
  time();
  window.setInterval(time, 15_000);

  // Mirror route changes (preact-iso uses pushState/replaceState) into the stage URL.
  for (const method of ["pushState", "replaceState"] as const) {
    const original = history[method].bind(history);
    history[method] = ((...args: Parameters<History["pushState"]>) => {
      original(...args);
      postUrl();
    }) as History["pushState"];
  }
  window.addEventListener("popstate", postUrl);
  postUrl();
}
